import React, { useState, useEffect } from 'react';
import { Container, Box, Grid, Typography, Card, CardContent, Button, CircularProgress, Stack, Tooltip, Fab } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { Settings, AddHomeWork, PersonAdd } from '@mui/icons-material';
import Layout from '../components/Layout';
import BannerCarousel from '../components/BannerCarousel';
import PropertyCard from '../components/PropertyCard';
import ServiceCard from '../components/ServiceCard';
import Footer from '../components/Footer';
import { apiService } from '../services/api';
import { useLanguage } from '../context/LanguageContext';
import { PROPERTY_CATEGORIES, SERVICE_CATEGORIES } from '../config/categories';

const Home = () => {
  const history = useHistory();
  const { t, language } = useLanguage();
  const [properties, setProperties] = useState([]);
  const [featuredServices, setFeaturedServices] = useState([]);
  const [services, setServices] = useState([]);
  const [loadingProperties, setLoadingProperties] = useState(true);
  const [loadingServices, setLoadingServices] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Fetch properties
  useEffect(() => {
    const fetchProperties = async () => {
      try {
        const response = await apiService.getProperties('all');
        if (response.success && response.data) {
          setProperties(response.data);
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
      } finally {
        setLoadingProperties(false);
      }
    };

    fetchProperties();
  }, []);

  // Fetch services
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await apiService.getServices('all');
        if (response.success && response.data) {
          setServices(response.data);
          setFeaturedServices(response.data.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        setLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  // Check authentication
  useEffect(() => {
    setIsAuthenticated(!!apiService.getAuthToken());
  }, []);

  const handleAdminClick = () => {
    if (isAuthenticated) {
      history.push('/admin-dashboard');
    } else {
      history.push('/login');
    }
  };

  // ===== OPEN IN NEW WINDOW =====
  const handlePropertyCategoryClick = (categoryValue) => {
    let url;
    if (categoryValue === 'all') {
      url = '/all-properties';
    } else {
      url = `/all-properties?category=${categoryValue}`;
    }
    history.push(url);
  };

  const handleServiceCategoryClick = (categoryValue) => {
    let url;
    if (categoryValue === 'all') {
      url = '/all-services';
    } else {
      url = `/all-services?category=${categoryValue}`;
    }
    history.push(url);
  };

  const handleViewAllProperties = () => {
    history.push('/all-properties');
  };

  const handleViewAllServices = () => {
    history.push('/all-services');
  };

  const handleBrowseProperties = () => {
    history.push('/browse-properties');
  };

  const handleBrowseServices = () => {
    history.push('/browse-services');
  };

  const handleSubmitProperty = () => {
    history.push('/submit');
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3, md: 6 }, px: { xs: 1, sm: 2 } }}>
        {/* Banner Carousel */}
        <BannerCarousel />

        {/* Welcome Section */}
        <Box sx={{ mt: { xs: 4, sm: 6, md: 8 }, mb: { xs: 4, sm: 6, md: 8 } }}>
          <Box sx={{ 
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: { xs: '8px', sm: '12px' },
            padding: { xs: '20px', sm: '28px', md: '40px' },
            color: 'white',
            textAlign: 'center',
            boxShadow: '0 10px 30px rgba(102, 126, 234, 0.3)'
          }}>
            <Typography 
              variant="h3" 
              sx={{ 
                fontWeight: 800, 
                mb: 2,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.8rem' },
                letterSpacing: '-0.5px',
                lineHeight: 1.3
              }}
            >
              {t.welcomeTitle}
            </Typography>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 300, 
                opacity: 0.95,
                fontSize: { xs: '0.95rem', sm: '1rem', md: '1.2rem' },
                lineHeight: 1.5
              }}
            >
              {t.welcomeSubtitle}
            </Typography>
          </Box>
        </Box>

        {/* Property Categories Grid */}
        <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: '#2d3748',
              mb: 4,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' }
            }}
          >
            🏠 Browse Properties by Category
          </Typography>

          <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }}>
            {/* Dynamic Category Cards */}
            {PROPERTY_CATEGORIES.map((category) => (
              <Grid item xs={3} sm={6} md={4} lg={3} key={category.value}>
                <Box
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                    },
                  }}
                  onClick={() => handlePropertyCategoryClick(category.value)}
                  title="Click to open in new window"
                >
                  {/* Icon Box */}
                  <Box
                    sx={{
                      background: 'white',
                      border: '1px solid #e2e8f0',
                      borderRadius: '12px',
                      padding: { xs: '16px', sm: '24px' },
                      textAlign: 'center',
                      marginBottom: '12px',
                      transition: 'all 0.3s ease',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      '&:hover': {
                        boxShadow: `0 12px 24px ${category.color}30`,
                      },
                    }}
                  >
                    <Typography sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem' }, lineHeight: 1 }}>
                      {category.icon}
                    </Typography>
                  </Box>
                  
                  {/* Text Below - Removed item count */}
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography 
                      sx={{ 
                        fontWeight: 700, 
                        fontSize: { xs: '0.75rem', sm: '0.95rem' },
                        color: '#2d3748',
                        lineHeight: 1.3
                      }}
                    >
                      {language === 'hi' 
                        ? category.labelHi?.replace(/^[^\s]*\s/, '').trim()
                        : category.labelEn?.replace(/^[^\s]*\s/, '').trim()}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}

            {/* View More Properties Card */}
            <Grid item xs={3} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '2px dashed #cbd5e0',
                  background: '#f7fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(102, 126, 234, 0.15)',
                    borderColor: '#667eea',
                  },
                }}
                onClick={handleBrowseProperties}
                title="Click to open in new window"
              >
                <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2.5 } }}>
                  <Typography variant="h3" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2.5rem' }, color: '#667eea' }}>
                    ➕
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.1rem' }, color: '#2d3748' }}>
                    More
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Service Categories Grid */}
        <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 700,
              color: '#2d3748',
              mb: 4,
              fontSize: { xs: '1.2rem', sm: '1.4rem', md: '1.6rem' }
            }}
          >
            👤 Browse Services by Category
          </Typography>

          <Grid container spacing={{ xs: 1, sm: 2.5, md: 3 }}>
            {/* Dynamic Service Category Cards */}
            {SERVICE_CATEGORIES.slice(0, 8).map((category) => {
              const icon = category.labelEn?.split(' ')[0] || '📋';
              return (
                <Grid item xs={3} sm={6} md={4} lg={3} key={category.value}>
                  <Box
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                      },
                    }}
                    onClick={() => handleServiceCategoryClick(category.value)}
                    title="Click to open in new window"
                  >
                    {/* Icon Box */}
                    <Box
                      sx={{
                        background: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '12px',
                        padding: { xs: '16px', sm: '24px' },
                        textAlign: 'center',
                        marginBottom: '12px',
                        transition: 'all 0.3s ease',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                        '&:hover': {
                          boxShadow: '0 12px 24px rgba(246, 173, 85, 0.2)',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '2.5rem', sm: '3.5rem' }, lineHeight: 1 }}>
                        {icon}
                      </Typography>
                    </Box>
                    
                    {/* Text Below - Removed item count */}
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography 
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: { xs: '0.75rem', sm: '0.95rem' },
                          color: '#2d3748',
                          lineHeight: 1.3
                        }}
                      >
                        {language === 'hi' 
                          ? category.labelHi?.replace(/^[^\s]*\s/, '').trim()
                          : category.labelEn?.replace(/^[^\s]*\s/, '').trim()}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              );
            })}

            {/* View More Services Card */}
            <Grid item xs={3} sm={6} md={4} lg={3}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: '2px dashed #cbd5e0',
                  background: '#f7fafc',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 24px rgba(246, 173, 85, 0.15)',
                    borderColor: '#f6ad55',
                  },
                }}
                onClick={handleBrowseServices}
                title="Click to open in new window"
              >
                <CardContent sx={{ textAlign: 'center', py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2.5 } }}>
                  <Typography variant="h3" sx={{ mb: 1, fontSize: { xs: '1.5rem', sm: '2.5rem' }, color: '#f6ad55' }}>
                    ➕
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, fontSize: { xs: '0.8rem', sm: '1.1rem' }, color: '#2d3748' }}>
                    More
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>

        {/* Featured Properties Section */}
        <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: { xs: 2, sm: 3, md: 4 } 
          }}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  color: '#2d3748',
                  mb: 1,
                  fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
                  borderLeft: '5px solid #667eea',
                  paddingLeft: { xs: '12px', sm: '16px' },
                  letterSpacing: '-0.5px',
                  wordBreak: 'break-word'
                }}
              >
                {t.featuredProperties}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#718096',
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                  paddingLeft: { xs: '12px', sm: '16px' },
                  fontWeight: 500
                }}
              >
                Discover our latest properties
              </Typography>
            </Box>
            
            {/* View All Button - Opens in New Window */}
            {properties.length > 3 && (
              <Button
                variant="contained"
                onClick={handleViewAllProperties}
                sx={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  fontWeight: 700,
                  py: { xs: 1, sm: 1.2 },
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                📍 {t.viewAllProperties}
              </Button>
            )}
          </Box>

          {loadingProperties ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : properties.length > 0 ? (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
              {properties.slice(0, 3).map((p) => (
                <Grid item key={p.id} xs={12} sm={6} md={4}>
                  <PropertyCard property={p} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid item xs={12}>
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No properties available yet
                </Typography>
              </Box>
            </Grid>
          )}
        </Box>

        {/* Featured Services Section */}
        <Box sx={{ mb: { xs: 4, sm: 6, md: 8 } }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 2, sm: 0 },
            mb: { xs: 2, sm: 3, md: 4 } 
          }}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  fontWeight: 800,
                  color: '#2d3748',
                  mb: 1,
                  fontSize: { xs: '1.4rem', sm: '1.8rem', md: '2.2rem' },
                  borderLeft: '5px solid #f6ad55',
                  paddingLeft: { xs: '12px', sm: '16px' },
                  letterSpacing: '-0.5px',
                  wordBreak: 'break-word'
                }}
              >
                {t.featuredExperts}
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: '#718096',
                  fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
                  paddingLeft: { xs: '12px', sm: '16px' },
                  fontWeight: 500
                }}
              >
                Connect with top professionals
              </Typography>
            </Box>

            {/* View All Services Button - Opens in New Window */}
            {services.length > 3 && (
              <Button
                variant="contained"
                onClick={handleViewAllServices}
                sx={{
                  background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                  color: 'white',
                  fontWeight: 700,
                  py: { xs: 1, sm: 1.2 },
                  px: { xs: 2, sm: 3 },
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    boxShadow: '0 8px 16px rgba(246, 173, 85, 0.4)',
                    transform: 'translateY(-2px)'
                  }
                }}
              >
                👤 {t.viewAllExperts}
              </Button>
            )}
          </Box>
          
          {loadingServices ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : featuredServices.length > 0 ? (
            <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
              {featuredServices.slice(0, 3).map((s) => (
                <Grid item key={s.id} xs={12} sm={6} md={4}>
                  <ServiceCard service={s} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>{t.notAvailable}</Typography>
            </Box>
          )}
        </Box>

        {/* CTA Section */}
        <Box sx={{ 
          mb: { xs: 4, sm: 6, md: 8 }, 
          py: { xs: 4, sm: 5, md: 6 }, 
          px: { xs: 2, sm: 3, md: 4 }, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          borderRadius: { xs: '8px', sm: '12px', md: '16px' },
          color: 'white'
        }}>
          <Typography 
            variant="h4" 
            sx={{ 
              fontWeight: 800, 
              mb: { xs: 2, sm: 3, md: 2 }, 
              textAlign: 'center', 
              fontSize: { xs: '1.3rem', sm: '1.8rem', md: '2.2rem' },
              lineHeight: 1.3,
              wordBreak: 'break-word'
            }}
          >
            {t.cta}
          </Typography>
          <Typography 
            variant="body1" 
            sx={{ 
              textAlign: 'center', 
              mb: { xs: 3, sm: 4, md: 4 }, 
              opacity: 0.95,
              maxWidth: '700px', 
              mx: 'auto',
              fontSize: { xs: '0.95rem', sm: '1rem', md: '1.05rem' },
              lineHeight: 1.6
            }}
          >
            {t.ctaText}
          </Typography>
          
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={{ xs: 2, sm: 2.5, md: 3 }} 
            sx={{ justifyContent: 'center', alignItems: 'stretch' }}
          >
            {/* List Property Card - Opens in New Window */}
            <Card
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: '100%', md: '320px' },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'rgba(255, 255, 255, 0.95)',
                '&:hover': {
                  transform: { xs: 'translateY(-4px)', sm: 'translateY(-8px)' },
                  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={handleSubmitProperty}
            >
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
                <AddHomeWork sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: '#667eea', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#2d3748', fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
                  {t.listProperty}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  {t.listPropertyDesc}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    fontWeight: 700,
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(102, 126, 234, 0.4)',
                    },
                  }}
                  onClick={handleSubmitProperty}
                >
                  {t.addProperty}
                </Button>
              </CardContent>
            </Card>

            {/* Become Expert Card - Opens in New Window */}
            <Card
              sx={{
                flex: 1,
                maxWidth: { xs: '100%', sm: '100%', md: '320px' },
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                background: 'rgba(255, 255, 255, 0.95)',
                '&:hover': {
                  transform: { xs: 'translateY(-4px)', sm: 'translateY(-8px)' },
                  boxShadow: '0 16px 32px rgba(0, 0, 0, 0.15)',
                },
              }}
              onClick={handleSubmitProperty}
            >
              <CardContent sx={{ textAlign: 'center', py: { xs: 3, sm: 4 }, px: { xs: 2, sm: 3 } }}>
                <PersonAdd sx={{ fontSize: { xs: 40, sm: 48, md: 56 }, color: '#f6ad55', mb: 2 }} />
                <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, color: '#2d3748', fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' } }}>
                  {t.becomeExpert}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3, lineHeight: 1.6, fontSize: { xs: '0.85rem', sm: '0.95rem' } }}>
                  {t.becomeExpertDesc}
                </Typography>
                <Button
                  variant="contained"
                  fullWidth
                  sx={{
                    background: 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
                    color: 'white',
                    fontWeight: 700,
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: '0.9rem', sm: '1rem' },
                    '&:hover': {
                      boxShadow: '0 8px 16px rgba(246, 173, 85, 0.4)',
                    },
                  }}
                  onClick={handleSubmitProperty}
                >
                  {t.addExpertProfile}
                </Button>
              </CardContent>
            </Card>
          </Stack>
        </Box>
      </Container>

      {/* Admin FAB Button */}
      <Tooltip title={isAuthenticated ? t.adminDashboard : t.loginToAdmin}>
        <Fab
          color="primary"
          aria-label="admin"
          onClick={handleAdminClick}
          sx={{
            position: 'fixed',
            bottom: { xs: 20, sm: 30 },
            right: { xs: 20, sm: 30 },
            background: isAuthenticated 
              ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
              : 'linear-gradient(135deg, #f6ad55 0%, #ed8936 100%)',
            boxShadow: isAuthenticated
              ? '0 8px 16px rgba(102, 126, 234, 0.4)'
              : '0 8px 16px rgba(246, 173, 85, 0.4)',
            width: { xs: 56, sm: 64 },
            height: { xs: 56, sm: 64 },
            '&:hover': { 
              boxShadow: isAuthenticated
                ? '0 12px 24px rgba(102, 126, 234, 0.6)'
                : '0 12px 24px rgba(246, 173, 85, 0.6)',
              transform: 'scale(1.1)'
            },
            zIndex: 999,
          }}
        >
          <Settings sx={{ fontSize: { xs: 24, sm: 28 } }} />
        </Fab>
      </Tooltip>

      {/* Footer Component */}
      <Footer />
    </Layout>
  );
};

export default Home;